import { Link } from "@tanstack/react-router";
import { StatusPill } from "@/components/shared/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useRoleHolders } from "@/hooks/use-roles";
import { originLinkSearch } from "@/lib/detail-origin";

/**
 * Everyone currently assigned this role, name + title, each linking to their
 * person page. Each link carries the role (and its "employees" tab) as origin so
 * the person page can breadcrumb / link straight back to this list.
 */
export function RoleEmployeesSection({ roleId, roleName }: { roleId: string; roleName: string }) {
  const { data = [], isLoading, isError } = useRoleHolders(roleId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return <p className="text-sm text-muted-foreground">Unable to load employees.</p>;
  }

  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground">No employees are assigned to this role.</p>;
  }

  return (
    <ul className="space-y-2">
      {data.map((holder) => (
        <li key={holder.id}>
          <Link
            to="/dashboard/persons/$personId"
            params={{ personId: holder.id }}
            search={originLinkSearch("role", roleId, roleName, "employees")}
            className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm transition-colors hover:bg-accent"
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-foreground">{holder.name}</span>
              {holder.title && (
                <span className="truncate text-xs text-muted-foreground">{holder.title}</span>
              )}
            </span>
            {holder.isPrimary && <StatusPill tone="success" label="Primary" />}
          </Link>
        </li>
      ))}
    </ul>
  );
}
